// filepath: apps/web/src/runtime/setDocumentLocale.ts
//
// FR-UI-CORE-9 — `<html lang>` + `<title>` helper.
//
// Single source of truth for the page-level locale + document
// metadata that screen readers and search engines read. Called
// from `main.tsx` on mount and on every `i18n.languageChanged`
// event. The static default in `index.html` matches the i18next
// boot locale (`ru`) so the initial paint is correct before
// `setDocumentLocale` runs.
//
// WCAG references:
//   - 2.4.2 Page Titled — `document.title` is set on every locale
//     change.
//   - 3.1.1 Language of Page — `document.documentElement.lang`
//     reflects the active locale.
//
// SECURITY: the title is sourced from the i18n bundle (platform
// chrome — not tenant-authored). No tenant data flows through
// this function.

import i18n from "@/i18n/i18n";
import type { Locale } from "@/i18n/useTranslation";

export type { Locale };

/**
 * Update `<html lang>` and `<title>` to match the supplied locale.
 * Falls back gracefully when `document` is undefined (e.g. SSR /
 * test environments that don't define a DOM).
 */
export function setDocumentLocale(locale: Locale): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  // `t('app.title', <fallback>)` — the fallback is used by i18next
  // when the key is absent. We provide an English title as the
  // ultimate fallback so screen readers always get a non-empty
  // string even before the i18n bundle is fully initialised.
  const title = i18n.t("app.title", "ASCOA — Generic Form Renderer");
  document.title = title;
}
